import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Building2,
  DoorClosed,
  ClipboardCheck,
  ShieldCheck,
  AlertTriangle,
  Clock,
  TrendingUp,
  BellRing,
  DoorOpen,
  Wrench,
  ChevronRight,
  KeyRound,
  Car,
  Navigation,
  CalendarClock,
  Sheet,
  Loader2,
  FileSpreadsheet,
} from "lucide-react";
import { isToday, isPast, parseISO } from "date-fns";
import { statisticsApi, invoicesApi, maintenanceApi, scheduleApi, buildingsApi, exportsApi } from "@/services/api";
import { useAsync } from "@/hooks/useAsync";
import { useAuth, userHasRole, isDualStaff, isInspectorLike } from "@/context/AuthContext";
import { fmtMoney, fmtDate, apiError } from "@/lib/format";
import { downloadBase64Envelope } from "@/lib/download";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { LoadingState, ErrorState } from "@/components/DataStates";
import { VerifyRepairDialog } from "@/components/VerifyRepairDialog";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

function BuildingSpreadsheetCard() {
  const buildings = useAsync(() => buildingsApi.getAll(), []);
  const [selected, setSelected] = useState("");
  const [busy, setBusy] = useState(false);
  const list = Array.isArray(buildings.data) ? buildings.data : [];

  const download = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      const res = await exportsApi.buildingInspectionsXlsx(selected);
      const name = (list.find((b) => String(b.id || b._id) === selected)?.name || "building")
        .replace(/[^a-z0-9]+/gi, "-").replace(/^-+|-+$/g, "") || "building";
      downloadBase64Envelope(res.data, `${name}-inspections.xlsx`);
      toast.success("Spreadsheet ready");
    } catch (e) {
      toast.error(apiError(e, "Could not create spreadsheet"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="mb-6 p-5" data-testid="dashboard-building-spreadsheet">
      <div className="flex items-center gap-3">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-emerald-50 text-emerald-700">
          <FileSpreadsheet className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <p className="font-semibold text-slate-900">Create building spreadsheet</p>
          <p className="text-sm text-slate-500">Pick a building to download its full inspection register (Excel).</p>
        </div>
      </div>
      <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center">
        <Select value={selected} onValueChange={setSelected}>
          <SelectTrigger className="sm:max-w-sm" data-testid="dashboard-spreadsheet-building">
            <SelectValue placeholder={buildings.loading ? "Loading buildings…" : "Select a building"} />
          </SelectTrigger>
          <SelectContent>
            {list.map((b) => (
              <SelectItem key={b.id || b._id} value={String(b.id || b._id)}>{b.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button onClick={download} disabled={!selected || busy} data-testid="dashboard-spreadsheet-download">
          {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sheet className="mr-2 h-4 w-4" />} Download spreadsheet
        </Button>
      </div>
    </Card>
  );
}

function StatCard({ icon: Icon, label, value, tone = "slate", testId, to }) {
  const tones = {
    slate: "bg-slate-100 text-slate-600",
    primary: "bg-primary/10 text-primary",
    emerald: "bg-emerald-100 text-emerald-600",
    red: "bg-red-100 text-red-600",
    amber: "bg-amber-100 text-amber-600",
  };
  const inner = (
    <Card className="flex items-center gap-4 p-5 transition-shadow hover:shadow-md" data-testid={testId}>
      <div className={`flex h-12 w-12 items-center justify-center rounded-xl ${tones[tone]}`}>
        <Icon className="h-6 w-6" />
      </div>
      <div>
        <p className="text-2xl font-bold text-slate-900">{value}</p>
        <p className="text-sm text-slate-500">{label}</p>
      </div>
    </Card>
  );
  return to ? <Link to={to}>{inner}</Link> : inner;
}

function formatAddress(address, postcode) {
  const a = (address || "").trim();
  const p = (postcode || "").trim();
  if (!a) return p || "No address on file";
  if (p && !a.toLowerCase().includes(p.toLowerCase())) return `${a}, ${p}`;
  return a;
}

function directionsUrl(address, postcode) {
  const a = (address || "").trim();
  const p = (postcode || "").trim();
  let dest = a;
  if (p && !a.toLowerCase().includes(p.toLowerCase())) dest = [a, p].filter(Boolean).join(" ").trim();
  if (!dest) dest = p;
  if (!dest) return null;
  return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(dest)}`;
}

// Launch the inspection flow pre-scoped to the booked building/doors.
function inspectionStartPath(insp) {
  const doors = Array.isArray(insp.door_ids) ? insp.door_ids.filter(Boolean) : [];
  const params = new URLSearchParams();
  if (insp.building_id) params.set("buildingId", insp.building_id);
  if (doors.length === 1) params.set("doorId", doors[0]);
  else if (doors.length > 1) params.set("doorIds", doors.join(","));
  return `/inspections/new?${params.toString()}`;
}

// Rank an inspection for the dashboard list so the most actionable ones
// surface first: today → soonest upcoming → most-recent overdue → unscheduled.
function rankInspection(i) {
  if (!i.scheduled_for) return { bucket: 3, t: 0 };
  let d;
  try {
    const s = i.scheduled_for;
    d = s.length === 10 ? parseISO(`${s}T00:00:00`) : parseISO(s);
  } catch { return { bucket: 3, t: 0 }; }
  if (isNaN(d.getTime())) return { bucket: 3, t: 0 };
  const day = new Date(d); day.setHours(0, 0, 0, 0);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const t = day.getTime();
  if (t === today.getTime()) return { bucket: 0, t };
  if (t > today.getTime()) return { bucket: 1, t };
  return { bucket: 2, t };
}

function MyInspectionCard({ insp, onStart }) {
  let whenLabel = insp.scheduled_for ? fmtDate(insp.scheduled_for) : "Unscheduled";
  let tone = "bg-slate-100 text-slate-700";
  try {
    if (insp.scheduled_for) {
      const s = insp.scheduled_for;
      const d = s.length === 10 ? parseISO(`${s}T00:00:00`) : parseISO(s);
      if (!isNaN(d.getTime())) {
        if (isToday(d)) { whenLabel = "Today"; tone = "bg-cyan-100 text-cyan-700"; }
        else if (isPast(d)) { whenLabel = `Overdue · ${fmtDate(insp.scheduled_for)}`; tone = "bg-red-100 text-red-700"; }
        else { tone = "bg-violet-100 text-violet-700"; }
      }
    }
  } catch { /* keep default */ }

  const dir = directionsUrl(insp.building_address, insp.building_postcode);
  const subset = Array.isArray(insp.door_ids) && insp.door_ids.length > 0;
  const doorText = insp.door_count
    ? `${insp.door_count} door${insp.door_count === 1 ? "" : "s"}${subset && insp.building_door_count ? ` of ${insp.building_door_count}` : ""}`
    : "Door count unknown";

  return (
    <Card className="flex flex-col p-4" data-testid={`my-inspection-${insp.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate font-semibold text-slate-900">{insp.building_name || "Inspection"}</p>
          <p className="truncate text-sm text-slate-500">
            {formatAddress(insp.building_address, insp.building_postcode)}
          </p>
        </div>
        <Badge className={`shrink-0 ${tone}`}>{whenLabel}</Badge>
      </div>

      <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1.5 text-sm text-slate-600">
        <span className="inline-flex items-center gap-1.5"><DoorClosed className="h-4 w-4 text-slate-400" /> {doorText}</span>
        {insp.inspected_count > 0 && insp.door_count > 0 && (
          <span className="inline-flex items-center gap-1.5 font-medium text-emerald-700" data-testid={`my-inspection-progress-${insp.id}`}>
            {insp.inspected_count} of {insp.door_count} inspected
          </span>
        )}
        {insp.access_codes && <span className="inline-flex items-center gap-1.5"><KeyRound className="h-4 w-4 text-slate-400" /> {insp.access_codes}</span>}
        {insp.parking_details && <span className="inline-flex items-center gap-1.5"><Car className="h-4 w-4 text-slate-400" /> {insp.parking_details}</span>}
      </div>

      {insp.notes && <p className="mt-2 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">{insp.notes}</p>}

      <div className="mt-3 flex flex-wrap gap-2">
        {dir && (
          <a href={dir} target="_blank" rel="noreferrer" data-testid={`my-inspection-directions-${insp.id}`}>
            <Button size="sm" variant="outline"><Navigation className="mr-1.5 h-4 w-4" /> Directions</Button>
          </a>
        )}
        <Button size="sm" onClick={onStart} data-testid={`my-inspection-start-${insp.id}`}><ClipboardCheck className="mr-1.5 h-4 w-4" /> Start inspection</Button>
      </div>
    </Card>
  );
}


export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { data, loading, error, reload } = useAsync(() => statisticsApi.get(), []);
  const invoices = useAsync(() => invoicesApi.list(), []);
  const canManage = user?.is_company_admin || userHasRole(user, "admin", "inspector");
  const reinspections = useAsync(
    () => (canManage ? statisticsApi.pendingReinspections() : Promise.resolve({ data: [] })),
    [canManage]
  );
  const pendingReinspections = Array.isArray(reinspections.data) ? reinspections.data : [];
  const [verifyDoor, setVerifyDoor] = useState(null);

  // Dual-role (engineer + inspector) staff get a compact "your engineer jobs"
  // badge so they see outstanding assigned work without switching modes.
  const dual = isDualStaff(user);
  const myJobs = useAsync(() => (dual ? maintenanceApi.list({ assigned_to_me: true }) : Promise.resolve({ data: [] })), [dual]);
  const myList = Array.isArray(myJobs.data) ? myJobs.data : [];
  const myOpen = myList.filter((j) => j.status !== "completed").length;
  const myToday = myList.filter((j) => {
    if (j.status === "completed" || !j.scheduled_for) return false;
    try {
      const s = j.scheduled_for;
      const d = s.length === 10 ? parseISO(`${s}T00:00:00`) : parseISO(s);
      return !isNaN(d.getTime()) && isToday(d);
    } catch { return false; }
  }).length;

  // Inspector's own upcoming inspection appointments — planning cards on the
  // dashboard (directions / access / door count) so they don't have to open
  // the schedule for each visit.
  const inspectorLike = isInspectorLike(user);
  const myInspAsync = useAsync(() => (inspectorLike ? scheduleApi.listInspections() : Promise.resolve({ data: [] })), [inspectorLike]);
  const myInspections = (Array.isArray(myInspAsync.data) ? myInspAsync.data : [])
    .filter((i) => i.inspector_id === user?.id && i.status !== "completed" && i.status !== "cancelled" && !i.is_complete)
    .sort((a, b) => {
      const ra = rankInspection(a);
      const rb = rankInspection(b);
      if (ra.bucket !== rb.bucket) return ra.bucket - rb.bucket;
      // Overdue: most-recent first. Today/upcoming: soonest first.
      return ra.bucket === 2 ? rb.t - ra.t : ra.t - rb.t;
    });

  const overdue = (Array.isArray(invoices.data) ? invoices.data : [])
    .filter((i) => i.overdue && i.status !== "paid" && i.status !== "cancelled")
    .sort((a, b) => (b.days_overdue || 0) - (a.days_overdue || 0));
  const overdueTotal = overdue.reduce((s, i) => s + Number(i.total || 0), 0);

  return (
    <div>
      <PageHeader
        title={`Welcome back, ${(user?.name || "").split(" ")[0] || ""}`.trim()}
        subtitle="Your fire-door compliance at a glance"
        testId="dashboard-header"
      />

      <BuildingSpreadsheetCard />


      {dual && !myJobs.loading && myOpen > 0 && (
        <Card className="mb-6 flex flex-wrap items-center justify-between gap-3 border-red-100 bg-red-50/40 p-4" data-testid="my-jobs-badge">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#ED1C24] text-white"><Wrench className="h-5 w-5" /></div>
            <div>
              <p className="font-semibold text-slate-900">
                You have {myOpen} engineer job{myOpen === 1 ? "" : "s"} assigned to you{myToday > 0 ? ` · ${myToday} due today` : ""}
              </p>
              <p className="text-sm text-slate-500">Switch to engineer mode to work through them.</p>
            </div>
          </div>
          <Button size="sm" variant="outline" onClick={() => navigate("/engineer")} data-testid="my-jobs-go">
            Engineer view <ChevronRight className="ml-1 h-4 w-4" />
          </Button>
        </Card>
      )}

      {inspectorLike && !myInspAsync.loading && myInspections.length > 0 && (
        <section className="mb-6" data-testid="my-inspections">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="flex items-center gap-2 text-lg font-bold text-slate-900">
              <CalendarClock className="h-5 w-5 text-primary" /> Your inspections
              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-sm font-semibold text-slate-600">{myInspections.length}</span>
            </h2>
            <Link to="/schedule" className="text-sm font-medium text-primary hover:underline" data-testid="my-inspections-schedule">View schedule</Link>
          </div>
          <div className="grid gap-3 md:grid-cols-2">
            {myInspections.slice(0, 6).map((insp) => (
              <MyInspectionCard key={insp.id} insp={insp} onStart={() => navigate(inspectionStartPath(insp))} />
            ))}
          </div>
          {myInspections.length > 6 && (
            <p className="mt-2 text-sm text-slate-500">+{myInspections.length - 6} more — <Link to="/schedule" className="font-medium text-primary hover:underline">open the schedule</Link></p>
          )}
        </section>
      )}

      {loading && <LoadingState label="Loading dashboard…" />}
      {!loading && error && <ErrorState message={error} onRetry={reload} />}

      {!loading && !error && data && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard icon={Building2} label="Buildings" value={data.total_buildings ?? 0} tone="primary" testId="stat-buildings" to="/buildings" />
            <StatCard icon={DoorClosed} label="Doors" value={data.total_doors ?? 0} tone="slate" testId="stat-doors" />
            <StatCard icon={ClipboardCheck} label="Inspections" value={data.total_inspections ?? 0} tone="slate" testId="stat-inspections" to="/inspections" />
            <StatCard icon={TrendingUp} label="Compliance rate" value={`${data.compliance_rate ?? 0}%`} tone="emerald" testId="stat-compliance" />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <StatCard icon={ShieldCheck} label="Passed inspections" value={data.pass_count ?? 0} tone="emerald" testId="stat-pass" />
            <StatCard icon={AlertTriangle} label="Failed inspections" value={data.fail_count ?? 0} tone="red" testId="stat-fail" />
            <StatCard icon={ClipboardCheck} label="Needs review" value={data.review_count ?? 0} tone="amber" testId="stat-review" />
            <StatCard icon={AlertTriangle} label="Overdue doors" value={data.overdue_count ?? 0} tone="red" testId="stat-overdue" />
            <StatCard icon={Clock} label="Due within 7 days" value={data.due_soon_count ?? 0} tone="amber" testId="stat-due-soon" />
            <StatCard icon={TrendingUp} label="Inspected (30 days)" value={data.recent_inspections_count ?? 0} tone="primary" testId="stat-recent" />
          </div>

          {!reinspections.loading && pendingReinspections.length > 0 && (
            <Card className="border-amber-100 p-5" data-testid="reinspection-card">
              <div className="mb-4 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-100 text-amber-600">
                    <ShieldCheck className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">Doors awaiting re-inspection · {pendingReinspections.length}</p>
                    <p className="text-sm text-slate-500">Remedial works complete — verify these fire doors</p>
                  </div>
                </div>
                <button onClick={() => navigate("/reinspections")} className="text-sm font-medium text-primary hover:underline" data-testid="reinspection-view-all">View all</button>
              </div>
              <div className="space-y-2">
                {pendingReinspections.slice(0, 5).map((d) => (
                  <div
                    key={d.id}
                    className="flex items-center justify-between gap-2 rounded-lg border border-slate-100 bg-slate-50 px-3 py-2 text-sm"
                    data-testid={`reinspection-door-${d.id}`}
                  >
                    <span className="min-w-0 truncate text-slate-700">
                      {d.door_reference || "Door"}{d.building_name ? ` · ${d.building_name}` : ""}
                      {d.location ? <span className="text-slate-400"> · {d.location}</span> : null}
                    </span>
                    <span className="flex shrink-0 items-center gap-1.5">
                      <Button size="sm" variant="outline" onClick={() => setVerifyDoor(d)} data-testid={`reinspection-verify-${d.id}`}>
                        <ShieldCheck className="mr-1 h-3.5 w-3.5" /> Verify
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => navigate(`/inspections/new?doorId=${d.id}`)} data-testid={`reinspection-inspect-${d.id}`}>
                        <DoorOpen className="mr-1 h-3.5 w-3.5" /> Inspect
                      </Button>
                    </span>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {!invoices.loading && overdue.length > 0 && (
            <Card className="border-red-100 p-5" data-testid="chase-card">
              <div className="mb-4 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-red-100 text-red-600">
                    <BellRing className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">Chase payments · {overdue.length} overdue</p>
                    <p className="text-sm text-slate-500">{fmtMoney(overdueTotal)} outstanding</p>
                  </div>
                </div>
                <Button size="sm" variant="outline" onClick={() => navigate("/invoices")} data-testid="chase-view-all">View all</Button>
              </div>
              <div className="space-y-2">
                {overdue.slice(0, 4).map((i) => (
                  <button
                    key={i.id}
                    onClick={() => navigate(`/invoices/${i.id}`)}
                    className="flex w-full items-center justify-between rounded-lg border border-slate-100 bg-slate-50 px-3 py-2 text-left text-sm hover:bg-slate-100"
                    data-testid={`chase-invoice-${i.id}`}
                  >
                    <span className="min-w-0 truncate text-slate-700">
                      {i.invoice_number} · {i.building_name || i.client_name || "—"}
                    </span>
                    <span className="flex shrink-0 items-center gap-2">
                      <span className="rounded bg-red-100 px-1.5 py-0.5 text-xs font-medium text-red-700">{i.days_overdue}d late</span>
                      <span className="font-semibold text-slate-800">{fmtMoney(i.total)}</span>
                    </span>
                  </button>
                ))}
              </div>
            </Card>
          )}
        </div>
      )}

      <VerifyRepairDialog
        doorId={verifyDoor?.id}
        doorRef={verifyDoor?.door_reference}
        open={!!verifyDoor}
        onOpenChange={(o) => !o && setVerifyDoor(null)}
        onDone={() => { setVerifyDoor(null); reinspections.reload(); }}
      />
    </div>
  );
}
